import { createSelector, Selector } from "@reduxjs/toolkit";
import React, { ReactNode, useCallback, useContext, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IsInterface, isObject } from "generic-type-guard";

import { ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { NEW_INSTANCE_IDENTIFIER } from "@com.mgmtp.a12.client/client-core/lib/core/application";
import { Model, ModelSelectors } from "@com.mgmtp.a12.client/client-core/lib/core/model";
import { FormActivity, FormEngineSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { OverviewEngineFactories } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/client-extensions";
import { ReferenceColumn } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/overview-model";
import { Button } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/components/button";
import { OverviewButton } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/components/overview-button";
import { TableBodyCell } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/components/table/sub-components/table-body-cell";
import { DefaultComponentMap } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/configuration/component-map";
import {
	DefaultWidgetMap,
	WidgetMap
} from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/configuration/widget-map";
import { LocalizerContext } from "@com.mgmtp.a12.utils/utils-localization-react/lib/main";
import { UaaSelectors } from "@com.mgmtp.a12.uaa/uaa-authentication-client";

import { getFVManifest } from "../../app/slices/fachverfahren/selectors";
import { AssigneeCandidate } from "../../app/slices/users/types";
import { assignProcessDocument, assignWiedervorlageDocument } from "../../modules/assignment/actions";
import { OverviewAssigneePicker } from "../../modules/assignment/components/AssigneePicker";
import { selectAssigneeCandidatesCreator } from "../../modules/assignment/selectors";
import { TASK_DETAIL_ACTIVITY_ID } from "../../modules/processOverview/store/actions";
import { isWiedervorlageDocument } from "../../modules/wiedervorlagen/types";
import { isDocumentWithProcessMetadata } from "../../modules/workflows/data/processMetadata";
import { convertAppIdToKey } from "../../utilities/applicationKeyUtils";
import { assertNonNullable } from "../../utilities/assert";
import { createExternalEnumLocalizerContextValue } from "../../utilities/externalEnumerationLocalizer";
import { hasUpdateDocumentPermission } from "../../utilities/permissionUtils";
import { selectHasNeededPermissions } from "../../utilities/user";
import { selectOperationLockStateByGroup } from "../../app/slices/operation/selectors";
import { DOCUMENT_LOCK_GROUP, isProcessLocked } from "../../utilities/lockProcessUtils";
import { lockProcessError } from "../../modules/processFormEngine/store/actions";

import { ActivityIdContext } from "../ActivityContext";
import { CustomContentBox } from "../CustomContentBox";
import { ScopeBasedVisibilityDecorator } from "../ScopeBasedVisibilityDecorator";

import { editOverviewDocument } from "./actions";
import { DocumentModelContextProvider } from "./context";
import { CustomOverviewHeading } from "./customHeading";
import { CustomTableBodyCell } from "./customTableBodyCell";

/**
 * A customized overview engine container that is displayed as an auxiliary to the main form (on the right-hand side).
 * The overview view also allows users to tick checkboxes and adds an edit button for each document.
 */
export function AuxiliaryOverviewEngine(props: OverviewEngineFactories.ViewComponentProps): ReactNode {
	const { activityId, name, modelDescriptors } = props;

	const modelId = useSelector(ActivitySelectors.activityPropById(activityId, a => a.descriptor.model));
	const dispatch = useDispatch();

	const fvId = useSelector(ActivitySelectors.activityPropById(activityId, a => a.descriptor.fachverfahren));
	const fvKey = fvId ? convertAppIdToKey(fvId) : undefined;

	const user = useSelector(UaaSelectors.user);
	const lockState = useSelector(selectOperationLockStateByGroup(DOCUMENT_LOCK_GROUP))[0];
	const processLocked = isProcessLocked(lockState, user);

	const readonly = useSelector(overviewEngineReadonlySelector(fvKey)) || processLocked;

	const eventHandlers = useMemo(
		() => ({
			onRowButtonClick(event: { documentId: string }) {
				if (!modelId) {
					throw new Error("Could not find document model in descriptor for activity " + activityId);
				}
				dispatch(
					editOverviewDocument({
						activityId: activityId,
						instanceId: event.documentId,
						modelId,
						fvKey,
						readonly,
						srcEvent: "BUTTON_CLICK"
					})
				);
			},
			onEventButtonClick() {
				if (!modelId) {
					throw new Error("Could not find document model in descriptor for activity " + activityId);
				}
				dispatch(
					editOverviewDocument({
						activityId,
						instanceId: NEW_INSTANCE_IDENTIFIER,
						modelId,
						fvKey,
						readonly,
						srcEvent: "EVENT_BUTTON"
					})
				);
			},
			onRowClick({ documentId }: { documentId: string }) {
				if (!modelId) {
					throw new Error("Could not find document model in descriptor for activity " + activityId);
				}
				dispatch(
					editOverviewDocument({
						activityId,
						instanceId: documentId,
						modelId,
						fvKey,
						readonly,
						srcEvent: "ROW_CLICK"
					})
				);
			}
		}),
		[modelId, activityId, dispatch, fvKey, readonly]
	);

	const customComponentMap = useMemo(
		() => ({
			...DefaultComponentMap,
			TableBodyCell: ExtendedCustomTableBodyCell,
			Heading: CustomOverviewHeading,
			OverviewButton: (props: OverviewButton.Props) => {
				const CustomOverviewButton = ScopeBasedVisibilityDecorator(
					DefaultComponentMap.OverviewButton,
					props => props.buttonModel.annotations,
					fvKey
				);
				return <>{!readonly && <CustomOverviewButton {...props} />}</>;
			},
			Button: (props: Button.PropsType) => {
				return <>{!readonly && <DefaultComponentMap.Button {...props} />}</>;
			}
		}),
		[fvKey, readonly]
	);

	const store = useSelector(store => store);
	const documentModel = useSelector(
		ModelSelectors.modelInScene({ activityId: activityId, modelType: "document" }, Model.isDocumentModel)
	);
	const { locale } = useContext(LocalizerContext);

	const localizerContextValue = React.useMemo(
		() => createExternalEnumLocalizerContextValue(locale, store, documentModel),
		[documentModel, locale, store]
	);

	if (!localizerContextValue) {
		return <OverviewEngineFactories.ViewComponent {...props} />;
	}

	return (
		<LocalizerContext.Provider value={localizerContextValue}>
			<ActivityIdContext.Provider value={activityId}>
				<DocumentModelContextProvider activityId={activityId}>
					<OverviewEngineFactories.ViewComponent
						key="wvl-overview"
						name={name}
						modelDescriptors={modelDescriptors}
						activityId={activityId}
						eventHandlers={eventHandlers}
						componentMap={customComponentMap}
						widgetMap={widgetsMap}
					/>
				</DocumentModelContextProvider>
			</ActivityIdContext.Provider>
		</LocalizerContext.Provider>
	);
}

function overviewEngineReadonlySelector(fvKey?: string): Selector<Record<string, unknown>, boolean, never> {
	return createSelector(
		[
			ActivitySelectors.data(TASK_DETAIL_ACTIVITY_ID),
			FormEngineSelectors.uiState(TASK_DETAIL_ACTIVITY_ID),
			selectHasNeededPermissions(hasUpdateDocumentPermission, fvKey)
		],
		(taskData, taskUiState, hasPermission) => {
			const isFinished =
				FormActivity.Data.SingleDocumentData.isInstance(taskData) &&
				isDocumentWithProcessMetadata(taskData.document)
					? taskData.document.root.processMetadata.isFinished
					: false;

			const isTaskReadOnly = (taskUiState?.readonly ?? false) && !isFinished;
			return isTaskReadOnly || !hasPermission;
		}
	);
}

const widgetsMap: WidgetMap = {
	...DefaultWidgetMap,
	ContentBox: CustomContentBox
};

const ASSIGNEE_COLUMN_ID = "column-assignee-id";

const ExtendedCustomTableBodyCell: React.ComponentType<TableBodyCell.Props> = function ExtendedCustomTableBodyCell(
	props
) {
	const dispatch = useDispatch();

	const activityId = useContext(ActivityIdContext);
	assertNonNullable(activityId);

	const fachverfahren = useSelector(
		ActivitySelectors.activityPropById(activityId, it => it.descriptor.fachverfahren)
	);

	const activityData = useSelector(ActivitySelectors.data(TASK_DETAIL_ACTIVITY_ID));

	const manifest = useSelector(getFVManifest(fachverfahren));
	const fvKey = manifest?.identification.key;

	const hasEditPermission = useSelector(selectHasNeededPermissions(hasUpdateDocumentPermission, fvKey));

	const docRef = props.row.id;

	const user = useSelector(UaaSelectors.user);
	const lockState = useSelector(selectOperationLockStateByGroup(DOCUMENT_LOCK_GROUP))[0];
	const processLocked = isProcessLocked(lockState, user);

	const readonly = useSelector(overviewEngineReadonlySelector(fvKey)) || processLocked;
	const DefaultComponent = useMemo(() => CustomTableBodyCell(readonly), [readonly]);

	const runtimeModules =
		isWiedervorlageDocument(props.row) && !props.row.root.Erledigt
			? "Wiedervorlagenzuweisung"
			: isDocumentWithProcessMetadata(props.row) &&
				  !props.row.root?.processMetadata.isFinished &&
				  !props.row.root?.processMetadata.archival?.archivalInProgress &&
				  !processLocked
				? "Vorgangszuweisung"
				: undefined;
	const candidates = useSelector(selectAssigneeCandidatesCreator(fvKey ?? "", runtimeModules));

	const fqn =
		isActivityWithDocument(activityData) && isDocumentWithProcessMetadata(activityData.document)
			? activityData.document.root.processMetadata.fachverfahren.fqn
			: undefined;

	const assigneeHtmlRef = useRef<HTMLElement | undefined>(undefined);
	const setAssigneeRef = useCallback((element: HTMLElement | null) => {
		assigneeHtmlRef.current = element ?? undefined;
	}, []);
	const onChange = useCallback<(assignee: AssigneeCandidate | undefined) => void>(
		assignee => {
			if (processLocked) {
				dispatch(lockProcessError({}));
				return;
			}
			if (runtimeModules === "Wiedervorlagenzuweisung") {
				dispatch(assignWiedervorlageDocument({ docRef, activityId, assignee }));
			} else if (runtimeModules === "Vorgangszuweisung") {
				dispatch(
					assignProcessDocument({
						docRef,
						activityId,
						assignee: assignee?.username ?? "",
						assigneeHtmlRef: assigneeHtmlRef.current
					})
				);
			}
		},
		[dispatch, activityId, docRef, runtimeModules]
	);

	const { row, columnModel } = props;

	if (
		fvKey &&
		ReferenceColumn.isAssignableFrom(columnModel) &&
		hasEditPermission &&
		columnModel.id === ASSIGNEE_COLUMN_ID
	) {
		if (runtimeModules) {
			return (
				<div style={fullWidthStyle} onClick={preventEvent}>
					<OverviewAssigneePicker
						id={`${activityId}-${row.id}-${columnModel.id}`}
						activityId={activityId}
						localizedModelText={columnModel.label}
						elementRef={columnModel.elementRef}
						document={row}
						fvKey={fvKey}
						fqn={fqn}
						candidates={candidates}
						onChange={onChange}
						inputRef={setAssigneeRef}
					/>
				</div>
			);
		}
	}

	return <DefaultComponent {...props} />;
};

const fullWidthStyle = { width: "100%" };

function preventEvent(event: React.MouseEvent): void {
	event.preventDefault();
	event.stopPropagation(); // Prevent event from bubbling up to the overview row
}

export const isActivityWithDocument = new IsInterface()
	.withProperties({
		document: isObject
	})
	.get();
